import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from '@chakra-ui/react';

import { useGlobalContext } from '../../subscriptions/state/GlobalContext';
import useForm from '../../utils/useForm';
import AddressForm from '../../components/Forms/AddressForm';

interface IProps {
  address?: ModalFormAddress;
  loading: boolean;
  onSubmit: (values: ModalFormAddress) => void;
  onCancel: () => void;
}

export default ({
  address,
  onCancel,
  onSubmit,
  loading,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();

  const { values, handleChange } = useForm<ModalFormAddress>({
    initialValues: {
      country: address?.country || '',
      province: address?.province || '',
      firstName: address?.firstName || '',
      lastName: address?.lastName || '',
      company: address?.company || '',
      address1: address?.address1 || '',
      address2: address?.address2 || '',
      city: address?.city || '',
      zip: address?.zip || '',
      phone: address?.phone || '',
    },
  });

  const { countries } = useGlobalContext();

  const isFormValid = useMemo(() => {
    const hasProvinces =
      countries.find(({ name }) => name === values.country)?.provinces?.length >
      0;

    return (
      !!values.address1 &&
      !!values.city &&
      !!values.country &&
      !!values.firstName &&
      !!values.lastName &&
      !!values.phone &&
      !!values.zip &&
      (!hasProvinces || !!values.province)
    );
  }, [countries, values]);

  const handleSubmit = async () => {
    onSubmit(values);
  };

  return (
    <>
      <ModalHeader>{t('subscription.update_shipping')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <form id="address-form" className="bundleapp-formcontainer">
          <AddressForm values={values} handleChange={handleChange} />
        </form>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={onCancel} variant="outlinedBig">
          {t('titles.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          disabled={loading || !isFormValid}
          variant="blackBig"
        >
          {t('titles.continue')}
        </Button>
      </ModalFooter>
    </>
  );
};
